import { useState } from 'react';
import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { createColumnHelper } from '@tanstack/react-table';
import { Search, TriangleAlert, Trash2, Plus, Pencil } from 'lucide-react';
import {
  SubjectListItem,
  SubjectsApi,
  Department,
  DepartmentsApi,
} from '@/services/academic.api';
import { PageHeader } from '@/components/ui/PageHeader';
import { Input, Textarea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Badge } from '@/components/ui/Badge';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { DataTable, useDataTable } from '@/components/data-table';
import type { PaginatedResult } from '@/components/data-table';

const subjectColumnHelper = createColumnHelper<SubjectListItem>();
const departmentColumnHelper = createColumnHelper<Department>();

function errMsg(e: unknown): string | undefined {
  if (!e) return undefined;
  const anyE = e as any;
  return anyE?.response?.data?.error?.message ?? anyE?.message ?? 'Something went wrong';
}

/** Wraps a small, unpaginated list in the same shape DataTable expects, so
 * Departments reuses the one shared table component rather than a second one. */
function asStaticPage<T>(items: T[]): PaginatedResult<T> {
  return { items, total: items.length, page: 1, limit: Math.max(items.length, 1), totalPages: 1 };
}

export function SubjectsPage() {
  const [tab, setTab] = useState<'subjects' | 'departments'>('subjects');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [deletingDepartment, setDeletingDepartment] = useState<Department | null>(null);
  const [deleteDepartmentError, setDeleteDepartmentError] = useState<string | undefined>();
  const [deptForm, setDeptForm] = useState<{ id: string | null; name: string; description: string } | null>(null);
  const [deptFormError, setDeptFormError] = useState<string | undefined>();
  const queryClient = useQueryClient();

  const dt = useDataTable({ defaultLimit: 20, defaultSortBy: 'name', defaultSortOrder: 'asc' });

  const { data, isFetching, error } = useQuery({
    queryKey: ['subjects', dt.params],
    queryFn: () =>
      SubjectsApi.list({
        ...dt.params,
        sortBy: dt.params.sortBy as 'name' | 'type' | 'classCount' | undefined,
      }),
    enabled: tab === 'subjects',
    placeholderData: keepPreviousData,
  });

  const { data: detail, isLoading: detailLoading } = useQuery({
    queryKey: ['subjects', detailId],
    queryFn: () => SubjectsApi.get(detailId as string),
    enabled: !!detailId,
  });

  const { data: departments, isFetching: departmentsFetching, error: departmentsError } = useQuery({
    queryKey: ['departments'],
    queryFn: DepartmentsApi.list,
    enabled: tab === 'departments',
  });

  const removeDepartment = useMutation({
    mutationFn: () => DepartmentsApi.remove(deletingDepartment!.id),
    onSuccess: () => {
      setDeletingDepartment(null);
      setDeleteDepartmentError(undefined);
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (e) => setDeleteDepartmentError(errMsg(e)),
  });

  const saveDepartment = useMutation({
    mutationFn: () => {
      const name = deptForm!.name.trim();
      const description = deptForm!.description.trim() || undefined;
      return deptForm!.id
        ? DepartmentsApi.update(deptForm!.id, { name, description })
        : DepartmentsApi.create({ name, description });
    },
    onSuccess: () => {
      setDeptForm(null);
      setDeptFormError(undefined);
      queryClient.invalidateQueries({ queryKey: ['departments'] });
    },
    onError: (e) => setDeptFormError(errMsg(e)),
  });

  const subjectColumns = [
    subjectColumnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => (
        <span className="text-slate-400">{dt.pagination.pageIndex * dt.pagination.pageSize + info.row.index + 1}</span>
      ),
    }),
    subjectColumnHelper.accessor('name', {
      header: 'Subject',
      enableSorting: true,
      cell: (info) => (
        <button
          type="button"
          onClick={() => setDetailId(info.row.original.id)}
          className="font-medium text-brand-600 hover:underline"
        >
          {info.getValue()}
        </button>
      ),
    }),
    subjectColumnHelper.accessor('type', {
      header: 'Type',
      enableSorting: true,
      cell: (info) => <Badge tone="slate">{info.getValue()}</Badge>,
    }),
    subjectColumnHelper.accessor('classCount', {
      header: 'Classes',
      enableSorting: true,
      cell: (info) =>
        info.getValue() > 0 ? (
          info.getValue()
        ) : (
          <span
            className="inline-flex items-center gap-1 text-amber-600"
            title="Not assigned to any class"
          >
            <TriangleAlert className="h-3.5 w-3.5" /> 0
          </span>
        ),
    }),
  ];

  const departmentColumns = [
    departmentColumnHelper.display({
      id: 'no',
      header: 'No.',
      enableSorting: false,
      cell: (info) => <span className="text-slate-400">{info.row.index + 1}</span>,
    }),
    departmentColumnHelper.accessor('name', {
      header: 'Department',
      cell: (info) => <span className="font-medium text-slate-900">{info.getValue()}</span>,
    }),
    departmentColumnHelper.accessor('teacherCount', {
      header: 'Teachers',
      cell: (info) => info.getValue(),
    }),
    departmentColumnHelper.display({
      id: 'actions',
      header: 'Action',
      cell: (info) => (
        <div className="flex items-center gap-1">
          <button
            type="button"
            title="Edit"
            className="rounded p-1 text-slate-500 hover:bg-slate-100 hover:text-slate-700"
            onClick={() => {
              setDeptFormError(undefined);
              setDeptForm({
                id: info.row.original.id,
                name: info.row.original.name,
                description: info.row.original.description ?? '',
              });
            }}
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            type="button"
            title="Delete"
            className="rounded p-1 text-slate-500 hover:bg-red-50 hover:text-red-600"
            onClick={() => {
              setDeleteDepartmentError(undefined);
              setDeletingDepartment(info.row.original);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      ),
    }),
  ];

  return (
    <div>
      <PageHeader
        title="Subjects"
        description="Subjects and the classes/teachers they're taught by."
      />

      <div className="mb-4 flex gap-1 border-b border-slate-200">
        <button
          type="button"
          onClick={() => setTab('subjects')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'subjects'
              ? 'border-b-2 border-brand-600 text-brand-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Subjects
        </button>
        <button
          type="button"
          onClick={() => setTab('departments')}
          className={`px-4 py-2 text-sm font-medium ${
            tab === 'departments'
              ? 'border-b-2 border-brand-600 text-brand-600'
              : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          Departments
        </button>
      </div>

      {tab === 'subjects' ? (
        error ? (
          <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
            {errMsg(error)}
          </div>
        ) : (
          <DataTable
            columns={subjectColumns}
            result={data}
            state={dt}
            loading={isFetching}
            emptyMessage={dt.params.search ? 'No subjects match your search.' : 'No subjects yet.'}
            toolbar={
              <div className="relative w-full sm:max-w-xs">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                <Input
                  value={dt.searchInput}
                  onChange={(e) => dt.setSearchInput(e.target.value)}
                  placeholder="Search subject name…"
                  className="pl-9"
                />
              </div>
            }
          />
        )
      ) : departmentsError ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {errMsg(departmentsError)}
        </div>
      ) : (
        <DataTable
          columns={departmentColumns}
          result={asStaticPage(departments ?? [])}
          state={dt}
          loading={departmentsFetching}
          emptyMessage="No departments set up for this school yet."
          toolbar={
            <button
              type="button"
              className="btn-primary flex items-center gap-1.5"
              onClick={() => {
                setDeptFormError(undefined);
                setDeptForm({ id: null, name: '', description: '' });
              }}
            >
              <Plus className="h-4 w-4" /> Add Department
            </button>
          }
        />
      )}

      <Modal
        open={!!detailId}
        onClose={() => setDetailId(null)}
        title={detail?.name ?? 'Subject'}
        description={detail ? detail.type : undefined}
        size="md"
      >
        {detailLoading ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : detail && detail.assignments.length > 0 ? (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="pb-2">Class</th>
                <th className="pb-2">Section</th>
                <th className="pb-2">Teacher</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {detail.assignments.map((a, i) => (
                <tr key={`${a.classId}-${a.sectionId ?? i}`}>
                  <td className="py-2 pr-2">
                    <Badge tone="blue">{a.className}</Badge>
                  </td>
                  <td className="py-2 pr-2">
                    {a.sectionName ? <Badge tone="slate">{a.sectionName}</Badge> : '—'}
                  </td>
                  <td className="py-2 text-slate-700">
                    {/* Rendered as-is, including known-doubled legacy names
                        (e.g. "Muneera T K Muneera T K") — see docs/data-issues.md,
                        not a bug to hide here. */}
                    {a.teacherName ?? <span className="text-slate-400">No teacher assigned</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : detail ? (
          <p className="text-sm text-slate-500">Not assigned to any class.</p>
        ) : null}
      </Modal>

      <Modal
        open={!!deptForm}
        onClose={() => setDeptForm(null)}
        title={deptForm?.id ? 'Edit Department' : 'Add Department'}
        size="sm"
      >
        {deptForm && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              saveDepartment.mutate();
            }}
            className="space-y-4"
          >
            {deptFormError && (
              <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                {deptFormError}
              </div>
            )}
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Name</label>
              <Input
                autoFocus
                value={deptForm.name}
                onChange={(e) => setDeptForm({ ...deptForm, name: e.target.value })}
                placeholder="e.g. Islamic Studies"
                required
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Description</label>
              <Textarea
                value={deptForm.description}
                onChange={(e) => setDeptForm({ ...deptForm, description: e.target.value })}
                rows={3}
                placeholder="Optional"
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" className="btn-secondary" onClick={() => setDeptForm(null)}>
                Cancel
              </button>
              <button
                type="submit"
                className="btn-primary"
                disabled={!deptForm.name.trim() || saveDepartment.isPending}
              >
                {saveDepartment.isPending ? 'Saving…' : 'Save'}
              </button>
            </div>
          </form>
        )}
      </Modal>

      <ConfirmDialog
        open={!!deletingDepartment}
        onClose={() => setDeletingDepartment(null)}
        onConfirm={() => removeDepartment.mutate()}
        title="Delete department?"
        message={
          deleteDepartmentError ??
          `This will permanently delete "${deletingDepartment?.name}". This cannot be undone.`
        }
        confirmText="Delete"
        destructive
        loading={removeDepartment.isPending}
      />
    </div>
  );
}
